import Styles from './Landing.module.css';
import { useEffect, useRef } from 'react';

/** Props for the ConstellationField canvas backdrop. */
interface IConstellationFieldProps {
  // Optional extra class merged onto the canvas (e.g. to tweak opacity per card).
  className?: string;
}

/** One drifting node in the network. Positions and velocities are in CSS pixels / CSS pixels per second. */
interface INode {
  x: number;
  y: number;
  velocityX: number;
  velocityY: number;
  // Pre-resolved "r, g, b" channel string for this node's dot fill.
  color: string;
}

/** File-local constants for the constellation field. */
const localConstants = {
  // Approximate canvas area (CSS px squared) allotted to each node; smaller divisor packs the field denser.
  areaPerNodePx: 17000,
  minNodes: 12,
  maxNodes: 80,
  // Node drift speed range, CSS px per second.
  minSpeedPxPerSec: 5,
  maxSpeedPxPerSec: 16,
  // Distance (CSS px) under which two nodes are joined; line alpha ramps to zero at this edge.
  linkDistancePx: 135,
  nodeRadiusPx: 1.6,
  lineWidthPx: 1,
  // Opacity ceilings for the joining lines and the dots themselves.
  maxLinkAlpha: 0.33,
  dotAlpha: 0.6,
  // Share of nodes tinted with the secondary (orange) accent instead of the primary (cyan).
  accentTwoFraction: 0.22,
  // CSS custom properties read for the two accent colors, with rgb fallbacks if they cannot be resolved.
  accentVar: '--landing-accent-rgb',
  accentTwoVar: '--landing-accent-2-rgb',
  fallbackAccentRgb: '0, 212, 255',
  fallbackAccentTwoRgb: '255, 107, 43',
  twoPi: Math.PI * 2,
  // Guard so a hidden/zero-size canvas never spawns work.
  minDimensionPx: 1,
  // Cap the per-frame delta so a backgrounded tab does not teleport every node on return.
  maxFrameSeconds: 0.05,
  millisecondsPerSecond: 1000
} as const;

/**
 * Animated constellation backdrop.
 *
 * Renders a field of slowly drifting dots onto a full-bleed canvas; any two dots closer than the link distance are joined by a line whose opacity fades with separation, giving the moving "lines
 * with dots" network effect. Node count scales with the canvas area, colors are read from the landing accent custom properties, and the whole thing is purely decorative (pointer-events are
 * disabled by the canvas style). Honors `prefers-reduced-motion` by painting a single static frame instead of animating.
 *
 * @param props - Component props.
 * @returns The canvas element hosting the network.
 */
export function ConstellationField(props: IConstellationFieldProps) {
  const { className } = props;
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const context = canvas.getContext('2d');
    if (!context) return;

    // Resolve accent channels from the cascade so the palette stays single-sourced in themes.css.
    const computed = getComputedStyle(canvas);
    const accentRgb = computed.getPropertyValue(localConstants.accentVar).trim() || localConstants.fallbackAccentRgb;
    const accentTwoRgb = computed.getPropertyValue(localConstants.accentTwoVar).trim() || localConstants.fallbackAccentTwoRgb;

    let nodes: INode[] = [];
    let cssWidth = 0;
    let cssHeight = 0;
    let rafId = 0;
    let lastTimestamp = 0;

    const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;

    const randomBetween = (low: number, high: number) => low + Math.random() * (high - low);

    // (Re)seed the node field for the current canvas size.
    const seedNodes = () => {
      const target = Math.round((cssWidth * cssHeight) / localConstants.areaPerNodePx);
      const wanted = Math.max(localConstants.minNodes, Math.min(localConstants.maxNodes, target));
      nodes = [];
      for (let position = 0; position < wanted; position += 1) {
        const angle = randomBetween(0, localConstants.twoPi);
        const speed = randomBetween(localConstants.minSpeedPxPerSec, localConstants.maxSpeedPxPerSec);
        nodes.push({
          x: randomBetween(0, cssWidth),
          y: randomBetween(0, cssHeight),
          velocityX: Math.cos(angle) * speed,
          velocityY: Math.sin(angle) * speed,
          color: Math.random() < localConstants.accentTwoFraction ? accentTwoRgb : accentRgb
        });
      }
    };

    // Draw one frame: lines first (under the dots), then the dots.
    const paint = () => {
      context.clearRect(0, 0, cssWidth, cssHeight);

      for (let outer = 0; outer < nodes.length; outer += 1) {
        const a = nodes[outer];
        for (let inner = outer + 1; inner < nodes.length; inner += 1) {
          const b = nodes[inner];
          const deltaX = a.x - b.x;
          const deltaY = a.y - b.y;
          const distance = Math.hypot(deltaX, deltaY);
          if (distance >= localConstants.linkDistancePx) continue;
          // Fade the line out as the two nodes separate, reaching zero exactly at the link distance.
          const strength = localConstants.maxLinkAlpha * (1 - distance / localConstants.linkDistancePx);
          context.strokeStyle = `rgba(${accentRgb}, ${strength})`;
          context.lineWidth = localConstants.lineWidthPx;
          context.beginPath();
          context.moveTo(a.x, a.y);
          context.lineTo(b.x, b.y);
          context.stroke();
        }
      }

      for (const node of nodes) {
        context.fillStyle = `rgba(${node.color}, ${localConstants.dotAlpha})`;
        context.beginPath();
        context.arc(node.x, node.y, localConstants.nodeRadiusPx, 0, localConstants.twoPi);
        context.fill();
      }
    };

    // Advance node positions, bouncing off the canvas edges to keep the field populated.
    const step = (seconds: number) => {
      for (const node of nodes) {
        node.x += node.velocityX * seconds;
        node.y += node.velocityY * seconds;
        if (node.x <= 0 || node.x >= cssWidth) node.velocityX = -node.velocityX;
        if (node.y <= 0 || node.y >= cssHeight) node.velocityY = -node.velocityY;
        node.x = Math.max(0, Math.min(cssWidth, node.x));
        node.y = Math.max(0, Math.min(cssHeight, node.y));
      }
    };

    const frame = (timestamp: number) => {
      const elapsed = lastTimestamp === 0 ? 0 : (timestamp - lastTimestamp) / localConstants.millisecondsPerSecond;
      lastTimestamp = timestamp;
      step(Math.min(elapsed, localConstants.maxFrameSeconds));
      paint();
      rafId = requestAnimationFrame(frame);
    };

    // Match the backing store to the element's CSS size and device pixel ratio for crisp lines.
    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      cssWidth = rect.width;
      cssHeight = rect.height;
      if (cssWidth < localConstants.minDimensionPx || cssHeight < localConstants.minDimensionPx) return;
      const ratio = window.devicePixelRatio || 1;
      canvas.width = Math.round(cssWidth * ratio);
      canvas.height = Math.round(cssHeight * ratio);
      context.setTransform(ratio, 0, 0, ratio, 0, 0);
      seedNodes();
      if (reduceMotion) paint();
    };

    const observer = new ResizeObserver(resize);
    observer.observe(canvas);
    resize();

    if (!reduceMotion) rafId = requestAnimationFrame(frame);

    return () => {
      observer.disconnect();
      if (rafId !== 0) cancelAnimationFrame(rafId);
    };
  }, []);

  return <canvas ref={canvasRef} className={className ? `${Styles.cardNetwork} ${className}` : Styles.cardNetwork} aria-hidden="true" />;
}
